import type { EntityColorOverrides, RoleOverrides, SchemeVariant, ThemeConfig } from "@app/core";
import type { StorageAdapter } from "../adapter/adapter.js";

/** CRUD over the `themes` table. JSON columns are parsed/serialized here so the
 *  rest of the app sees plain {@link ThemeConfig} objects. Mirrors CalendarsRepository. */
export class ThemesRepository {
  constructor(private readonly db: StorageAdapter) {}

  async insert(t: ThemeConfig): Promise<void> {
    await this.db.execute(
      `INSERT INTO themes (id,preset,variant,source,source_dark,role_overrides,entity_palette,
        palette_overrides,entity_overrides,block_tint_diff,block_gradient,chroma_boost,auto_color_calendars,created_at,updated_at,deleted_at)
       VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)`,
      params(t),
    );
  }

  async update(t: ThemeConfig): Promise<void> {
    await this.db.execute(
      `UPDATE themes SET preset=?,variant=?,source=?,source_dark=?,role_overrides=?,entity_palette=?,
        palette_overrides=?,entity_overrides=?,block_tint_diff=?,block_gradient=?,chroma_boost=?,auto_color_calendars=?,updated_at=?,deleted_at=? WHERE id=?`,
      [t.preset, t.variant, t.source, t.sourceDark ?? null,
       JSON.stringify(t.roleOverrides), JSON.stringify(t.entityPalette),
       JSON.stringify(t.paletteOverrides), JSON.stringify(t.entity),
       t.blockTintDiff, t.blockGradient ? 1 : 0, t.chromaBoost, t.autoColorCalendars ? 1 : 0, t.updated_at, t.deleted_at, t.id],
    );
  }

  async listActive(): Promise<ThemeConfig[]> {
    const rows = await this.db.select<Record<string, unknown>>(`SELECT * FROM themes WHERE deleted_at IS NULL`);
    return rows.map(fromRow);
  }

  /** Every row INCLUDING soft-deleted tombstones — for sync. */
  async listAll(): Promise<ThemeConfig[]> {
    const rows = await this.db.select<Record<string, unknown>>(`SELECT * FROM themes`);
    return rows.map(fromRow);
  }

  async findById(id: string): Promise<ThemeConfig | null> {
    const rows = await this.db.select<Record<string, unknown>>(`SELECT * FROM themes WHERE id=?`, [id]);
    return rows[0] ? fromRow(rows[0]) : null;
  }

  async hardDelete(id: string): Promise<void> {
    await this.db.execute(`DELETE FROM themes WHERE id=?`, [id]);
  }
}

function params(t: ThemeConfig): (string | number | null)[] {
  return [
    t.id, t.preset, t.variant, t.source, t.sourceDark ?? null,
    JSON.stringify(t.roleOverrides), JSON.stringify(t.entityPalette),
    JSON.stringify(t.paletteOverrides), JSON.stringify(t.entity),
    t.blockTintDiff, t.blockGradient ? 1 : 0, t.chromaBoost, t.autoColorCalendars ? 1 : 0,
    t.created_at, t.updated_at, t.deleted_at,
  ];
}

function parseJson<T>(value: unknown, fallback: T): T {
  if (typeof value !== "string" || value.length === 0) return fallback;
  try { return JSON.parse(value) as T; } catch { return fallback; }
}

function fromRow(r: Record<string, unknown>): ThemeConfig {
  return {
    id: String(r["id"]),
    preset: String(r["preset"] ?? "custom"),
    variant: String(r["variant"] ?? "tonal_spot") as SchemeVariant,
    source: String(r["source"] ?? "#4255c7"),
    sourceDark: (r["source_dark"] as string | null) ?? null,
    roleOverrides: parseJson<RoleOverrides>(r["role_overrides"], { light: {}, dark: {} }),
    entityPalette: parseJson<string[]>(r["entity_palette"], []),
    paletteOverrides: parseJson<Record<number, string>>(r["palette_overrides"], {}),
    entity: parseJson<EntityColorOverrides>(r["entity_overrides"], {}),
    blockTintDiff: Number(r["block_tint_diff"] ?? 0.22),
    blockGradient: Number(r["block_gradient"] ?? 1) === 1,
    chromaBoost: Number(r["chroma_boost"] ?? 0),
    autoColorCalendars: Number(r["auto_color_calendars"] ?? 1) === 1,
    created_at: String(r["created_at"]),
    updated_at: String(r["updated_at"]),
    deleted_at: (r["deleted_at"] as string | null) ?? null,
  };
}
